/**
 * A task that NEVER runs concurrently with itself.
 *
 * WHY THIS EXISTS. A read-derive-write cycle (read an inbox, derive a value,
 * write it) is only correct if nothing else is doing the same thing at the same
 * time on the same target: two cycles started a few milliseconds apart both read
 * before either writes, and the one that finishes last puts ITS (older) reading
 * back on the document. The owner's participation materializer had two
 * independent triggers — the connection and the inbox push — and nothing between
 * them.
 *
 * COALESCING. A request made while a run is in flight does not queue behind an
 * unbounded chain: at most ONE follow-up run is scheduled, and every request
 * made during the current run shares it. That is sound precisely because a run
 * re-derives everything from the current state — one follow-up observes whatever
 * the N requests were about. What a run must NOT be is incremental (a `+1`); the
 * caller keeps that property, this primitive assumes it.
 *
 * WHAT A CALLER GETS BACK. The promise of the run that will serve its request —
 * the running one when it started idle, the coalesced follow-up otherwise. It
 * settles with that run's outcome, so a failure is never swallowed here; a
 * caller that does not await must attach its own rejection handler.
 */

interface Settle {
  promise: Promise<void>;
  resolve(): void;
  reject(err: unknown): void;
}

function settleLater(): Settle {
  let resolve!: () => void;
  let reject!: (err: unknown) => void;
  const promise = new Promise<void>((res, rej) => {
    resolve = res;
    reject = rej;
  });
  return { promise, resolve, reject };
}

export function createSerialTask<Reason>(
  run: (reason: Reason) => Promise<void>,
): (reason: Reason) => Promise<void> {
  let busy = false;
  let queued: { reason: Reason; settle: Settle } | null = null;

  const pump = async (reason: Reason, settle: Settle): Promise<void> => {
    busy = true;
    try {
      await run(reason);
      settle.resolve();
    } catch (err) {
      settle.reject(err);
    } finally {
      busy = false;
      const next = queued;
      queued = null;
      // A failed run must not wedge the task: the follow-up starts either way.
      if (next) void pump(next.reason, next.settle);
    }
  };

  return (reason: Reason): Promise<void> => {
    if (!busy) {
      const settle = settleLater();
      void pump(reason, settle);
      return settle.promise;
    }
    // Already running: one follow-up is enough for every request made meanwhile.
    // The FIRST such request names it — the ones that join it are, by definition,
    // asking for the same thing.
    if (!queued) queued = { reason, settle: settleLater() };
    return queued.settle.promise;
  };
}
